import React from 'react';
import { Grid, Skeleton, Box, Stack, Card, CardContent, useTheme } from '@mui/material';

interface SkeletonCardProps {
  children?: React.ReactNode;
  height?: string | number;
  sx?: any;
  contentSx?: any;
}

const DashboardPageSkeleton = () => {
  const theme = useTheme();

  const GRID_SPACING = { xs: 1, sm: 2 };

  const SkeletonCard = ({ children, height, sx = {}, contentSx = {} }: SkeletonCardProps) => (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        ...sx
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          p: { xs: 1.5, md: 2 },
          '&:last-child': { pb: { xs: 1.5, md: 2 } },
          ...contentSx
        }}
      >
        {children ? children : <Skeleton variant="rectangular" height={height || '100%'} sx={{ borderRadius: 2 }} />}
      </CardContent>
    </Card>
  );

  const StatBlock = () => (
    <Stack spacing={1} justifyContent="center" height="100%">
      <Skeleton variant="text" width="40%" height={40} />
      <Skeleton variant="text" width="80%" height={40} />
      <Box display="flex" gap={1}>
        <Skeleton variant="text" width="30%" height={20} />
        <Skeleton variant="text" width="40%" height={20} />
      </Box>
    </Stack>
  );

  return (
    <Box sx={{ p: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={{ xs: 2, md: 3 }} ml={{ xs: 0.5, md: 1.5 }}>
        <Skeleton variant="text" width={160} height={60} />
        <Skeleton variant="rectangular" width={200} height={40} sx={{ borderRadius: 2 }} />
      </Box>

      <Grid container spacing={GRID_SPACING} mb={{ xs: 2, md: 3 }}>
        {[...Array(4)].map((_, index) => (
          <Grid item xs={6} sm={6} md={3} key={`top-${index}`}>
            <SkeletonCard>
              <StatBlock />
            </SkeletonCard>
          </Grid>
        ))}
      </Grid>

      <Stack spacing={{ xs: 2, md: 3 }}>
        <Box
          sx={{
            width: '100%',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '20px',
            padding: { xs: '10px', md: '15px' }
          }}
        >
          <Box sx={{ mb: { xs: '10px', md: '15px' } }}>
            <Grid container spacing={GRID_SPACING}>
              <Grid item xs={12} md={4}>
                <SkeletonCard sx={{ minHeight: 320 }}>
                  <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between">
                      <Skeleton width={180} height={30} variant="rectangular" />
                      <Skeleton width={80} height={30} variant="rectangular" sx={{ borderRadius: 2 }} />
                    </Box>
                    <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 2 }} />
                  </Stack>
                  <Grid container spacing={2} mt={1}>
                    {[...Array(4)].map((_, i) => (
                      <Grid item xs={6} key={i}>
                        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3 }} />
                      </Grid>
                    ))}
                  </Grid>
                </SkeletonCard>
              </Grid>
              <Grid item xs={12} md={8}>
                <SkeletonCard sx={{ minHeight: 320 }}>
                  <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between">
                      <Skeleton width={180} height={30} variant="rectangular" />
                      <Skeleton width={80} height={30} variant="rectangular" sx={{ borderRadius: 2 }} />
                    </Box>
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} variant="rectangular" height={45} sx={{ borderRadius: 2 }} />
                    ))}
                  </Stack>
                </SkeletonCard>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={GRID_SPACING}>
            {[...Array(4)].map((_, index) => (
              <Grid item xs={6} sm={6} md={3} key={`copier-stat-${index}`}>
                <SkeletonCard>
                  <StatBlock />
                </SkeletonCard>
              </Grid>
            ))}
          </Grid>
        </Box>

        <SkeletonCard sx={{ minHeight: 320 }}>
          <Stack spacing={1}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              flexDirection={{ xs: 'column', sm: 'row' }}
            >
              <Box width={{ xs: '100%', sm: 'auto' }}>
                <Skeleton width={220} height={40} />
              </Box>

              <Box display="flex" gap={1} mt={{ xs: 1.5, sm: 0 }} width={{ xs: '100%', sm: 'auto' }}>
                <Skeleton variant="rectangular" height={35} sx={{ borderRadius: '8px', flex: 1, maxWidth: { sm: 140 } }} />
                <Skeleton variant="rectangular" height={35} sx={{ borderRadius: '8px', flex: 1, maxWidth: { sm: 140 } }} />
              </Box>
            </Box>

            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={45} sx={{ borderRadius: 1.5 }} />
            ))}
          </Stack>
        </SkeletonCard>
      </Stack>
    </Box>
  );
};

export default DashboardPageSkeleton;
